import { createApp, h } from 'vue'
import Konva from 'konva'
import HaspObject from './HaspObject'
import Model3dPreview from '../components/model3d/Model3dPreview.vue'
import { BORDER_SIDE_FULL, resolveBorderGeometry } from '../draw/borderGeometry'

export default class HaspH3d extends HaspObject {
    constructor(config) {
        if (config.obj === 'h3d') config.obj = '3d'
        config.width ??= 320
        config.height ??= 240
        super(config)
        this._createKonvaPreview()

        requestAnimationFrame(() => this._mountPreview())
        this.on('resource', () => this._renderPreview())
        this.on('camera_state', () => this._applyStoredCameraState())
        this.on('destroy', () => this._destroyPreview())

        const update = () => this._schedulePreviewLayout()
        this.on('dragmove transform transformend xChange yChange widthChange heightChange visibleChange hidden', update)
        for (const prop of ['radius', 'border_width', 'border_side']) {
            this.onStyle('main', prop, update)
        }
    }

    setNewSize(size) {
        super.setNewSize(size)
        this._syncKonvaPreviewSize()
        this._schedulePreviewLayout()
    }

    _createKonvaPreview() {
        this.previewClipGroup = new Konva.Group({ listening: false })
        this.previewClipGroup.clipFunc(context => this._clipPreview(context))
        this.previewImage = new Konva.Image({ listening: false })
        this.previewStatus = new Konva.Text({
            align: 'center',
            verticalAlign: 'middle',
            fill: '#94a3b8',
            fontFamily: 'Roboto Condensed',
            fontSize: 12,
            listening: false,
            text: this.resource ? 'Loading 3D preview...' : 'Select a 3D model resource',
        })
        this.previewClipGroup.add(this.previewImage)
        this.previewClipGroup.add(this.previewStatus)
        this.add(this.previewClipGroup)
        this.previewClipGroup.zIndex(this.rect.zIndex() + 1)
        this._syncKonvaPreviewSize()
        this.idTextObj.moveToTop()
    }

    _clipPreview(context) {
        const { width, height, radius } = this._previewContentBox()
        context.beginPath()
        context.moveTo(radius, 0)
        context.arcTo(width, 0, width, height, radius)
        context.arcTo(width, height, 0, height, radius)
        context.arcTo(0, height, 0, 0, radius)
        context.arcTo(0, 0, width, 0, radius)
        context.closePath()
    }

    _syncKonvaPreviewSize() {
        if (!this.previewImage || !this.previewStatus) return
        const box = this._previewContentBox()
        this.previewClipGroup.setAttrs({ x: box.x, y: box.y })
        this.previewImage.setAttrs({ x: 0, y: 0, width: box.width, height: box.height })
        this.previewStatus.setAttrs({ x: 0, y: 0, width: box.width, height: box.height, padding: Math.min(8, box.width / 8) })
        this.previewClipGroup.zIndex(this.rect.zIndex() + 1)
        this.idTextObj.moveToTop()
        this.previewClipGroup?.getLayer()?.batchDraw()
    }

    _previewContentBox() {
        const width = Math.max(0, Number(this.rect?.width?.() ?? this.width?.() ?? 0))
        const height = Math.max(0, Number(this.rect?.height?.() ?? this.height?.() ?? 0))
        const geom = resolveBorderGeometry({
            width,
            height,
            radius: this.styleProp('main', 'radius'),
            borderWidth: this.styleProp('main', 'border_width'),
            borderSide: this.styleProp('main', 'border_side') ?? BORDER_SIDE_FULL,
        })
        const content = geom.visible && !geom.solid ? clampRect(geom.inner, width, height) : geom.outer
        return {
            ...content,
            radius: Math.max(0, Math.min(geom.visible ? geom.rin : geom.rout, content.width / 2, content.height / 2)),
        }
    }

    _mountPreview() {
        const stage = this.getStage()
        const host = stage?.container()
        if (!host) {
            requestAnimationFrame(() => this._mountPreview())
            return
        }

        host.style.position ||= 'relative'
        const konvaContent = host.querySelector('.konvajs-content')
        if (konvaContent) {
            konvaContent.style.position ||= 'relative'
            konvaContent.style.zIndex = '1'
        }

        this.previewEl = document.createElement('div')
        this.previewEl.className = 'hasp-h3d-preview'
        Object.assign(this.previewEl.style, {
            position: 'absolute',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: '0',
            visibility: 'hidden',
            margin: '0',
            padding: '0',
            transform: 'none',
        })

        this.previewContentEl = document.createElement('div')
        Object.assign(this.previewContentEl.style, {
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
        })
        this.previewEl.appendChild(this.previewContentEl)

        this.interactionEl = document.createElement('div')
        Object.assign(this.interactionEl.style, {
            position: 'absolute',
            pointerEvents: 'none',
            zIndex: '2',
            margin: '0',
            padding: '0',
            background: 'transparent',
            cursor: 'default',
            touchAction: 'none',
        })

        this.orbitButtonEl = document.createElement('button')
        this.orbitButtonEl.type = 'button'
        this.orbitButtonEl.innerHTML = `
            <svg viewBox="0 0 24 24" aria-hidden="true" style="width: 100%; height: 100%; display: block;">
                <path d="M4.8 13.2c1.5 4.3 7.8 6 12.8 3.5 3.2-1.6 4.8-4.3 3.6-6.3-.8-1.4-2.7-2-5-1.8"
                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M16.3 5.8l-.4 3.1 3.1.4" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M19.2 10.8c-1.5-4.3-7.8-6-12.8-3.5-3.2 1.6-4.8 4.3-3.6 6.3.8 1.4 2.7 2 5 1.8"
                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M7.7 18.2l.4-3.1-3.1-.4" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`
        this.orbitButtonEl.title = 'Toggle 3D orbit interaction'
        this.orbitButtonEl.setAttribute('aria-label', 'Toggle 3D orbit interaction')
        Object.assign(this.orbitButtonEl.style, {
            position: 'absolute',
            border: '0',
            outline: '0',
            background: 'transparent',
            color: '#e2e8f0',
            padding: '0',
            pointerEvents: 'auto',
            cursor: 'pointer',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))',
            zIndex: '3',
        })
        this.orbitButtonEl.addEventListener('pointerdown', this._onOrbitButtonPointerDown = (event) => {
            event.preventDefault()
            event.stopPropagation()
        })
        this.orbitButtonEl.addEventListener('click', this._onOrbitButtonClick = (event) => {
            event.preventDefault()
            event.stopPropagation()
            if (!this.previewSelected) this.getLayer()?.setSelectedNode(this)
            this._setOrbitMode(!this.orbitMode)
        })

        this.resetButtonEl = document.createElement('button')
        this.resetButtonEl.type = 'button'
        this.resetButtonEl.innerHTML = `
            <svg viewBox="0 0 24 24" aria-hidden="true" style="width: 100%; height: 100%; display: block;">
                <path d="M8 4H4v4M16 4h4v4M20 16v4h-4M8 20H4v-4"
                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="12" cy="12" r="2.5" fill="none" stroke="currentColor" stroke-width="2"/>
            </svg>`
        this.resetButtonEl.title = 'Reset and fit 3D view'
        this.resetButtonEl.setAttribute('aria-label', 'Reset and fit 3D view')
        Object.assign(this.resetButtonEl.style, {
            position: 'absolute',
            border: '0',
            outline: '0',
            background: 'transparent',
            color: '#e2e8f0',
            padding: '0',
            pointerEvents: 'auto',
            cursor: 'pointer',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))',
            zIndex: '3',
        })
        this.resetButtonEl.addEventListener('pointerdown', this._onResetButtonPointerDown = (event) => {
            event.preventDefault()
            event.stopPropagation()
        })
        this.resetButtonEl.addEventListener('click', this._onResetButtonClick = (event) => {
            event.preventDefault()
            event.stopPropagation()
            if (!this.previewSelected) this.getLayer()?.setSelectedNode(this)
            this._resetCameraView()
        })

        this._setOrbitMode(false)
        host.prepend(this.previewEl)
        host.appendChild(this.interactionEl)
        host.appendChild(this.orbitButtonEl)
        host.appendChild(this.resetButtonEl)
        stage.on(`wheel.h3d-${this.id()} dragmove.h3d-${this.id()} dragend.h3d-${this.id()} xChange.h3d-${this.id()} yChange.h3d-${this.id()} scaleXChange.h3d-${this.id()} scaleYChange.h3d-${this.id()}`, () => this._schedulePreviewLayout())
        this._watchOrbitKeys()
        this._watchAncestorLayout()
        this._renderPreview()
        this._schedulePreviewLayout()
    }

    _renderPreview() {
        if (!this.previewContentEl) return
        this.previewApp?.unmount()
        this._setPreviewStatus(this.resource ? 'Loading 3D preview...' : 'Select a 3D model resource')
        this.previewApp = createApp({
            render: () => this.resource
                ? h(Model3dPreview, {
                    modelId: this.resource,
                    fill: true,
                    borderless: true,
                    controlsElement: this.interactionEl,
                    backgroundColor: 'transparent',
                    sceneBackgroundColor: '#000000',
                    sceneBackgroundOpacity: 0,
                    preserveDrawingBuffer: true,
                    onCanvasReady: canvas => this._setPreviewCanvas(canvas),
                    onFrameRendered: () => this.previewClipGroup?.getLayer()?.batchDraw(),
                    onStatusChange: status => this._setPreviewStatus(status),
                    cameraState: this.camera_state,
                    onCameraChange: state => this._queueCameraState(state),
                    onViewReady: api => { this.previewViewApi = api },
                    // Published for the preview action dispatcher; cleared on unmount and
                    // on every resource reload so no stale THREE.Light is ever addressed.
                    onLightsReady: api => { this.previewLightsApi = api },
                })
                : h('div'),
        })
        this.previewApp.mount(this.previewContentEl)
    }

    _setPreviewCanvas(canvas) {
        this.previewImage?.image(canvas || null)
        this.previewClipGroup?.getLayer()?.batchDraw()
    }

    _setPreviewStatus(status) {
        if (!this.previewStatus) return
        this.previewStatus.text(status || '')
        this.previewStatus.visible(Boolean(status))
        this.previewClipGroup?.getLayer()?.batchDraw()
    }

    _schedulePreviewLayout() {
        cancelAnimationFrame(this._layoutFrame)
        this._layoutFrame = requestAnimationFrame(() => this._updatePreviewLayout())
    }

    _updatePreviewLayout() {
        if (!this.previewEl || !this.interactionEl || !this.orbitButtonEl || !this.resetButtonEl) return
        const stage = this.getStage()
        const host = stage?.container()
        if (!stage || !host || !this.isVisible()) {
            this._setOrbitMode(false)
            this.previewEl.style.display = 'none'
            this.interactionEl.style.display = 'none'
            this.orbitButtonEl.style.display = 'none'
            this.resetButtonEl.style.display = 'none'
            return
        }

        const outerBox = this.rect.getClientRect({ relativeTo: stage })
        const contentBox = this._previewContentBox()
        const scaleX = stage.scaleX() || 1
        const scaleY = stage.scaleY() || scaleX
        const stagePos = stage.position()
        const outerLeft = stagePos.x + outerBox.x * scaleX
        const outerTop = stagePos.y + outerBox.y * scaleY
        const outerWidth = Math.max(0, outerBox.width * scaleX)
        const contentLeft = outerLeft + contentBox.x * scaleX
        const contentTop = outerTop + contentBox.y * scaleY
        const contentWidth = Math.max(0, contentBox.width * scaleX)
        const contentHeight = Math.max(0, contentBox.height * scaleY)
        const radius = Math.max(0, contentBox.radius * Math.min(scaleX, scaleY))

        this._syncKonvaPreviewSize()
        this.previewEl.style.display = 'block'
        this._positionEl(this.previewEl, contentLeft, contentTop, contentWidth, contentHeight)
        this.previewEl.style.borderRadius = `${radius}px`

        this.interactionEl.style.display = 'block'
        this._positionEl(this.interactionEl, contentLeft, contentTop, contentWidth, contentHeight)

        this.orbitButtonEl.style.display = 'block'
        this._updateOrbitButtonGeometry(outerLeft, outerTop, outerWidth, scaleX, scaleY)
        this.resetButtonEl.style.display = this.orbitMode ? 'block' : 'none'
    }

    _positionEl(el, left, top, width, height) {
        el.style.left = `${left}px`
        el.style.top = `${top}px`
        el.style.width = `${width}px`
        el.style.height = `${height}px`
    }

    _setOrbitMode(active) {
        if (!active && this.orbitMode) this._flushCameraState()
        this.orbitMode = Boolean(active)
        if (this.interactionEl) {
            this.interactionEl.style.pointerEvents = this.orbitMode ? 'auto' : 'none'
            this.interactionEl.style.cursor = this.orbitMode ? 'grab' : 'default'
        }
        if (this.resetButtonEl) this.resetButtonEl.style.display = this.orbitMode ? 'block' : 'none'
        this._updateOrbitButtonStyle()
    }

    _queueCameraState(state) {
        if (!Array.isArray(state) || state.length < 8) return
        this._pendingCameraState = state.slice(0, 8)
        clearTimeout(this._cameraStateTimer)
        this._cameraStateTimer = setTimeout(() => this._flushCameraState(), 300)
    }

    _flushCameraState() {
        clearTimeout(this._cameraStateTimer)
        this._cameraStateTimer = null
        if (!this._pendingCameraState) return
        const state = this._pendingCameraState
        this._pendingCameraState = null
        this._commitCameraState(state)
    }

    _commitCameraState(state) {
        const next = Array.isArray(state) ? state.slice(0, 8) : null
        if (JSON.stringify(this.camera_state ?? null) === JSON.stringify(next)) return
        const layer = this.getLayer?.()
        if (layer && !layer._historySuppress) layer.layerCallback('camera-state', this)
        this.camera_state = next
        this._updateOrbitButtonStyle()
    }

    _applyStoredCameraState() {
        if (!this.previewViewApi) return
        if (Array.isArray(this.camera_state)) this.previewViewApi.applyView(this.camera_state)
        else this.previewViewApi.resetView()
        this._updateOrbitButtonStyle()
    }

    _resetCameraView() {
        clearTimeout(this._cameraStateTimer)
        this._cameraStateTimer = null
        this._pendingCameraState = null
        this._commitCameraState(null)
        this.previewViewApi?.resetView()
    }

    _watchOrbitKeys() {
        this._onOrbitKeyDown = (event) => {
            if (event.key === 'Escape') {
                this._setOrbitMode(false)
                return
            }
            if (event.key !== 'Tab' || !this.previewSelected) return
            const host = this.getStage()?.container()
            const active = document.activeElement
            if (active !== host && active !== document.body) return
            event.preventDefault()
            this._setOrbitMode(!this.orbitMode)
        }
        window.addEventListener('keydown', this._onOrbitKeyDown)
        window.addEventListener('blur', this._onWindowBlur = () => this._setOrbitMode(false))
    }

    setSelected(selected) {
        super.setSelected(selected)
        this.previewSelected = Boolean(selected)
        if (!selected) this._setOrbitMode(false)
        else this._updateOrbitButtonStyle()
    }

    _updateOrbitButtonStyle() {
        if (!this.orbitButtonEl) return
        if (this.orbitMode) {
            this.orbitButtonEl.style.color = '#7dd3fc'
            this.orbitButtonEl.style.filter = 'drop-shadow(0 0 4px rgba(56, 189, 248, 0.95)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))'
        } else if (this.previewSelected) {
            this.orbitButtonEl.style.color = '#e0f2fe'
            this.orbitButtonEl.style.filter = 'drop-shadow(0 0 3px rgba(125, 211, 252, 0.55)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))'
        } else {
            this.orbitButtonEl.style.color = '#e2e8f0'
            this.orbitButtonEl.style.filter = 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))'
        }
        this._updateResetButtonStyle()
    }

    _updateResetButtonStyle() {
        if (!this.resetButtonEl) return
        const hasCustomView = Array.isArray(this.camera_state)
        this.resetButtonEl.style.color = hasCustomView ? '#7dd3fc' : '#e2e8f0'
        this.resetButtonEl.style.filter = hasCustomView
            ? 'drop-shadow(0 0 3px rgba(56, 189, 248, 0.75)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))'
            : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))'
    }

    _updateOrbitButtonGeometry(left, top, width, scaleX, scaleY) {
        const scale = Math.max(0.35, Math.min(scaleX || 1, scaleY || scaleX || 1))
        const size = Math.round(22 * scale)
        const margin = Math.round(5 * scale)
        this.orbitButtonEl.style.width = `${size}px`
        this.orbitButtonEl.style.height = `${size}px`
        this.orbitButtonEl.style.left = `${left + width - size - margin}px`
        this.orbitButtonEl.style.top = `${top + margin}px`
        this.resetButtonEl.style.width = `${size}px`
        this.resetButtonEl.style.height = `${size}px`
        this.resetButtonEl.style.left = `${left + width - (size * 2) - (margin * 2)}px`
        this.resetButtonEl.style.top = `${top + margin}px`
    }

    _watchAncestorLayout() {
        const namespace = `.h3d-${this.id()}`
        this._ancestorLayoutNodes = []
        let node = this.getParent()
        while (node) {
            node.on(`visibleChange${namespace} xChange${namespace} yChange${namespace}`, () => this._schedulePreviewLayout())
            this._ancestorLayoutNodes.push(node)
            node = node.getParent?.()
        }
    }

    _destroyPreview() {
        cancelAnimationFrame(this._layoutFrame)
        clearTimeout(this._cameraStateTimer)
        this.getStage()?.off(`.h3d-${this.id()}`)
        this._ancestorLayoutNodes?.forEach(node => node.off(`.h3d-${this.id()}`))
        window.removeEventListener('keydown', this._onOrbitKeyDown)
        window.removeEventListener('blur', this._onWindowBlur)
        this.orbitButtonEl?.removeEventListener('pointerdown', this._onOrbitButtonPointerDown)
        this.orbitButtonEl?.removeEventListener('click', this._onOrbitButtonClick)
        this.resetButtonEl?.removeEventListener('pointerdown', this._onResetButtonPointerDown)
        this.resetButtonEl?.removeEventListener('click', this._onResetButtonClick)
        this.previewApp?.unmount()
        this.previewClipGroup?.destroy()
        this.previewEl?.remove()
        this.interactionEl?.remove()
        this.orbitButtonEl?.remove()
        this.resetButtonEl?.remove()
        this.previewApp = null
        this.previewLightsApi = null
        this.previewClipGroup = null
        this.previewImage = null
        this.previewStatus = null
        this.previewEl = null
        this.previewContentEl = null
        this.interactionEl = null
        this.orbitButtonEl = null
        this.resetButtonEl = null
        this.previewViewApi = null
        this._pendingCameraState = null
        this._ancestorLayoutNodes = null
    }
}

function clampRect(rect, width, height) {
    const x1 = Math.max(0, Math.min(width, rect.x))
    const y1 = Math.max(0, Math.min(height, rect.y))
    const x2 = Math.max(0, Math.min(width, rect.x + rect.width))
    const y2 = Math.max(0, Math.min(height, rect.y + rect.height))
    return {
        x: x1,
        y: y1,
        width: Math.max(0, x2 - x1),
        height: Math.max(0, y2 - y1),
    }
}
